import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import SecurityIcon from '@mui/icons-material/Security';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';

import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import EInvoiceSelector from '../components/EInvoiceSelector';
import TaiwanAddressSelector from '../components/TaiwanAddressSelector';
import { trackTikTokInitiateCheckout } from '../components/TikTokPixel';
import { getTemplateStyles } from '../config/templateStyles';

const Checkout = () => {
  const { t } = useTranslation('checkout');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { cart, sessionId, clearCart } = useCart();
  const { setAuthFromCheckout, isAuthenticated, user } = useAuth();
  const { templateId } = useStore();
  const tplStyles = getTemplateStyles(templateId);

  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [emapLoading, setEmapLoading] = useState(false);
  const [blockedUrl, setBlockedUrl] = useState('');

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [shippingConfig, setShippingConfig] = useState({ base_fee: 60, free_shipping_threshold: 0 });

  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    shipping_method: 'cvs',
    store_type: '7-11',
    store_id: '',
    store_name: '',
    store_address: '',
    home_city: '',
    home_district: '',
    home_zipcode: '',
    home_address: '',
    remark: '',
    payment_method: '',
  });

  const [invoiceData, setInvoiceData] = useState({
    type: 'carrier',
    carrierType: 'EJ0113',
    carrierId: null,
    buyerId: null,
    companyName: null,
    loveCode: null,
    donate: false,
  });

  const subtotal = cart?.subtotal || 0;
  const shippingFee =
    shippingConfig.free_shipping_threshold > 0 && subtotal >= shippingConfig.free_shipping_threshold
      ? 0
      : Number(shippingConfig.base_fee || 0);
  const total = subtotal + shippingFee;

  const hasTrackedCheckout = useRef(false);
  const hasTrackedShipping = useRef(false);
  const hasTrackedPayment = useRef(false);

  useEffect(() => {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      navigate('/cart');
      return;
    }

    if (hasTrackedCheckout.current) return;
    hasTrackedCheckout.current = true;

    trackTikTokInitiateCheckout(cart);

    if (window.dataLayer) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'TWD',
          value: cart.subtotal,
          items: cart.items.map((item) => ({
            item_id: item.sku,
            item_name: item.name,
            price: item.sale_price,
            quantity: item.quantity,
          })),
        },
      });
    }

    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_type: 'product',
        content_ids: cart.items.map((i) => i.sku),
        value: cart.subtotal,
        currency: 'TWD',
        num_items: cart.items.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [paymentRes, shippingRes] = await Promise.all([
          api.get('/settings/payment-methods/active'),
          api.get('/settings/shipping'),
        ]);

        if (paymentRes.data?.success && Array.isArray(paymentRes.data.payment_methods) && paymentRes.data.payment_methods.length > 0) {
          setPaymentMethods(paymentRes.data.payment_methods);
          setForm((p) => ({ ...p, payment_method: paymentRes.data.payment_methods[0].code }));
        } else {
          setPaymentMethods([{ code: 'cod', name: t('payment.cod'), description: t('payment.cod_desc'), icon: '💰' }]);
          setForm((p) => ({ ...p, payment_method: 'cod' }));
        }

        if (shippingRes.data?.success && shippingRes.data.config) {
          setShippingConfig({
            base_fee: Number(shippingRes.data.config.base_fee) || 60,
            free_shipping_threshold: Number(shippingRes.data.config.free_shipping_threshold) || 0,
          });
        }
      } catch (e) {
        setPaymentMethods([{ code: 'cod', name: t('payment.cod'), description: t('payment.cod_desc'), icon: '💰' }]);
        setForm((p) => ({ ...p, payment_method: 'cod' }));
      } finally {
        setLoadingPayments(false);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  useEffect(() => {
    if (isAuthenticated && user) {
      setForm((p) => ({
        ...p,
        customer_phone: user.phone || p.customer_phone,
        customer_name: user.name || p.customer_name,
        customer_email: user.email || p.customer_email,
      }));
      setPhoneVerified(true);
      return;
    }

    const savedPhone = localStorage.getItem('verified_phone');
    const savedName = localStorage.getItem('customer_name');
    if (savedPhone) {
      setForm((p) => ({ ...p, customer_phone: savedPhone, customer_name: savedName || p.customer_name }));
      setPhoneVerified(true);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const handleStoreMessage = (event) => {
      if (event.data && event.data.type === 'CVS_STORE_SELECTED') {
        const store = event.data.store;
        if (store?.name) {
          setForm((p) => ({
            ...p,
            store_id: store.id || '',
            store_name: store.name,
            store_address: store.address || '',
          }));
          setEmapLoading(false);
          toast.success(t('shipping.store_selected', { name: store.name }));

          if (!hasTrackedShipping.current && cart && window.dataLayer) {
            hasTrackedShipping.current = true;
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
              event: 'add_shipping_info',
              ecommerce: {
                currency: 'TWD',
                value: cart.subtotal,
                shipping_tier: 'cvs',
                items: cart.items.map((item) => ({
                  item_id: item.sku,
                  item_name: item.name,
                  price: item.sale_price,
                  quantity: item.quantity,
                })),
              },
            });
          }
        }
      }
    };

    window.addEventListener('message', handleStoreMessage);
    return () => window.removeEventListener('message', handleStoreMessage);
  }, [cart]);

  const sendVerificationCode = async () => {
    if (!/^09\d{8}$/.test(form.customer_phone)) {
      toast.error(t('form.invalid_phone'));
      return;
    }
    setVerifying(true);
    try {
      const res = await api.post('/auth/send-code', { phone: form.customer_phone });
      if (res.data?.success) {
        setShowVerification(true);
        setCountdown(60);
        toast.success(t('form.code_sent'));
        if (res.data.code) toast.info(`Dev code: ${res.data.code}`, { autoClose: 10000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.error || t('form.code_send_failed'));
    } finally {
      setVerifying(false);
    }
  };

  const verifyPhone = async () => {
    if (!verificationCode || verificationCode.length !== 4) {
      toast.error(t('form.invalid_code'));
      return;
    }
    setVerifying(true);
    try {
      const res = await api.post('/auth/verify-code', { phone: form.customer_phone, code: verificationCode });
      if (res.data?.success) {
        setPhoneVerified(true);
        localStorage.setItem('verified_phone', form.customer_phone);
        toast.success(t('form.phone_verified'));
        if (res.data.token && res.data.user) setAuthFromCheckout(res.data.token, res.data.user);
        if (res.data.user?.name) {
          setForm((p) => ({ ...p, customer_name: res.data.user.name }));
          localStorage.setItem('customer_name', res.data.user.name);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.error || t('form.verify_failed'));
    } finally {
      setVerifying(false);
    }
  };

  const handleOpenStoreMap = async () => {
    // 預先開啟空視窗，避免等 API 回傳後才開導致被瀏覽器阻擋
    const w = window.open('', 'StoreMap', 'width=900,height=700,scrollbars=yes,resizable=yes');
    try {
      setEmapLoading(true);
      const res = await api.get('/stores/smilepay-emap', { params: { store_type: form.store_type } });
      if (res.data?.success && res.data.map_url) {
        if (!w) {
          // 如果連同步開啟都被擋，則顯示手動點擊 Dialog
          setBlockedUrl(res.data.map_url);
          setEmapLoading(false);
        } else {
          w.location.href = res.data.map_url;
          const i = setInterval(() => {
            if (w.closed) {
              clearInterval(i);
              setEmapLoading(false);
            }
          }, 1000);
        }
      } else {
        if (w) w.close();
        toast.error(t('shipping.map_failed'));
        setEmapLoading(false);
      }
    } catch (e) {
      if (w) w.close();
      toast.error(t('shipping.map_failed'));
      setEmapLoading(false);
    }
  };

  const submitButtonText = useMemo(() => {
    const m = form.payment_method;
    if (m === 'linepay') return t('confirm.pay_linepay');
    if (m === 'credit' || m === 'credit_card') return t('confirm.pay_credit');
    if (m === 'atm') return t('confirm.pay_atm');
    if (m === 'cvs' || m === 'cvs_barcode' || m === 'smilepay') return t('confirm.pay_cvs');
    return t('confirm.place_order');
  }, [form.payment_method, t]);

  const validateBeforeSubmit = () => {
    if (!phoneVerified) {
      toast.error(t('form.phone_required'));
      return false;
    }
    if (!form.customer_name.trim()) {
      toast.error(t('form.name_required'));
      return false;
    }
    if (form.shipping_method === 'cvs') {
      if (!form.store_name || !form.store_address) {
        toast.error(t('shipping.store_required'));
        return false;
      }
    }
    if (form.shipping_method === 'home') {
      if (!form.home_city || !form.home_address) {
        toast.error(t('shipping.address_required'));
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateBeforeSubmit()) return;

    if (!hasTrackedShipping.current && cart && window.dataLayer) {
      hasTrackedShipping.current = true;
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_shipping_info',
        ecommerce: {
          currency: 'TWD',
          value: cart.subtotal,
          shipping_tier: form.shipping_method === 'home' ? 'home_delivery' : 'cvs',
          items: cart.items.map((item) => ({
            item_id: item.sku,
            item_name: item.name,
            price: item.sale_price,
            quantity: item.quantity,
          })),
        },
      });
    }

    setLoading(true);
    try {
      localStorage.setItem('customer_name', form.customer_name);

      const orderRes = await api.post(
        '/orders',
        { ...form, shipping_fee: shippingFee, invoice_data: invoiceData },
        { headers: { 'x-session-id': sessionId } }
      );

      if (!orderRes.data?.success) throw new Error(orderRes.data?.error || t('confirm.order_failed'));

      const orderNumber = orderRes.data.order?.order_no || orderRes.data.order?.order_number;
      if (!orderNumber) throw new Error(t('confirm.no_order_number'));

      const pay = form.payment_method;

      if (!hasTrackedPayment.current && cart && window.dataLayer) {
        hasTrackedPayment.current = true;
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'add_payment_info',
          ecommerce: {
            currency: 'TWD',
            value: cart.subtotal,
            payment_type: pay,
            items: cart.items.map((item) => ({
              item_id: item.sku,
              item_name: item.name,
              price: item.sale_price,
              quantity: item.quantity,
            })),
          },
        });
      }

      if (pay === 'cod') {
        await clearCart();
        toast.success(t('confirm.order_success'));
        navigate(`/checkout/complete/${orderNumber}`);
        return;
      }

      if (pay === 'linepay') {
        const r = await api.post('/linepay/request', { order_no: orderNumber, amount: total });
        if (r.data?.success && r.data.paymentUrl) {
          window.location.href = r.data.paymentUrl;
          return;
        }
        throw new Error(r.data?.error || t('confirm.linepay_failed'));
      }

      if (pay === 'credit' || pay === 'credit_card') {
        const r = await api.post('/smilepay/payment/credit-card', { order_no: orderNumber });
        if (!r.data?.success) throw new Error(r.data?.error || t('confirm.credit_failed'));

        const formEl = document.createElement('form');
        formEl.method = 'POST';
        formEl.action = r.data.formAction;
        formEl.style.display = 'none';
        Object.entries(r.data.formData || {}).forEach(([k, v]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = k;
          input.value = v;
          formEl.appendChild(input);
        });
        document.body.appendChild(formEl);
        await clearCart();
        formEl.submit();
        return;
      }

      if (pay === 'atm') {
        const r = await api.post('/smilepay/payment/atm', { order_no: orderNumber });
        if (!r.data?.success) throw new Error(r.data?.error || t('confirm.atm_failed'));
        await clearCart();
        toast.success(t('confirm.atm_success'));

        const payInfo = r.data.atmType === 'manual'
          ? {
            type: 'manual_atm',
            bankName: r.data.bankName,
            bankCode: r.data.bankCode,
            bankAccount: r.data.bankAccount,
            bankHolder: r.data.bankHolder,
            deadline: r.data.payEndDate,
          }
          : {
            type: 'atm',
            bankName: r.data.bankName,
            bankNo: r.data.atmBankNo,
            accountNo: r.data.atmNo,
            deadline: r.data.payEndDate,
          };

        navigate(`/checkout/complete/${orderNumber}`, {
          state: { paymentInfo: payInfo },
        });
        return;
      }

      if (pay === 'cvs' || pay === 'cvs_barcode' || pay === 'smilepay') {
        const r = await api.post('/smilepay/payment/cvs', { order_no: orderNumber });
        if (!r.data?.success) throw new Error(r.data?.error || t('confirm.cvs_failed'));
        await clearCart();
        toast.success(t('confirm.cvs_success'));
        navigate(`/checkout/complete/${orderNumber}`, {
          state: {
            paymentInfo: {
              type: 'cvs_barcode',
              barcode1: r.data.barcode1,
              barcode2: r.data.barcode2,
              barcode3: r.data.barcode3,
              deadline: r.data.payEndDate,
            },
          },
        });
        return;
      }

      if (pay === 'c2c') {
        const r = await api.post('/smilepay/payment/c2c', {
          order_no: orderNumber,
          store_type: form.store_type,
          store_id: form.store_id,
          store_name: form.store_name,
          store_address: form.store_address,
        });
        if (!r.data?.success) throw new Error(r.data?.error || t('confirm.c2c_failed'));
        await clearCart();
        toast.success(t('confirm.order_success'));
        navigate(`/checkout/complete/${orderNumber}`);
        return;
      }

      await clearCart();
      navigate(`/checkout/complete/${orderNumber}`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || t('confirm.order_failed'));
    } finally {
      setLoading(false);
    }
  };

  const summary = (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: tplStyles.card.borderRadius,
        border: '1px solid rgba(16,42,67,0.1)',
        boxShadow: '0 14px 32px rgba(16,42,67,0.08)',
        position: { md: 'sticky' },
        top: { md: 100 },
      }}
      elevation={0}
    >
      <Typography fontWeight={950}>{t('order_summary')}</Typography>
      <Divider sx={{ my: 1.5 }} />
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between">
          <Typography color="text.secondary">{t('cart.subtotal')}</Typography>
          <Typography>${Number(subtotal).toLocaleString()}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography color="text.secondary">{t('cart.shipping_fee')}</Typography>
          <Typography>{shippingFee === 0 ? t('cart.free_shipping') : `$${Number(shippingFee).toLocaleString()}`}</Typography>
        </Stack>
        <Divider sx={{ my: 1 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography fontWeight={900}>{t('cart.total')}</Typography>
          <Typography fontWeight={950} color="error" fontSize="1.35rem">
            ${Number(total).toLocaleString()}
          </Typography>
        </Stack>
      </Stack>

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleSubmit}
        data-testid="checkout-submit"
        disabled={loading || loadingPayments || !phoneVerified}
        sx={{
          mt: 2,
          py: 1.4,
          fontWeight: 950,
          borderRadius: tplStyles.button.borderRadius,
          textTransform: tplStyles.button.textTransform,
          letterSpacing: tplStyles.button.letterSpacing || 'normal',
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : submitButtonText}
      </Button>

      {!phoneVerified && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
          {t('form.phone_verify_required')}
        </Typography>
      )}

      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SecurityIcon sx={{ color: 'success.main' }} />
            <Typography variant="body2" fontWeight={900}>
              {t('confirm.secure_checkout')}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <LocalShippingIcon sx={{ color: 'primary.main' }} />
            <Typography variant="caption" color="text.secondary">
              {t('confirm.shipping_estimate')}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleIcon sx={{ color: 'success.main' }} />
            <Typography variant="caption" color="text.secondary">
              {t('confirm.transparent_shipping')}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );

  if (!cart || !Array.isArray(cart.items)) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>{t('loading')}</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ pb: isMobile ? 12 : 0 }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Typography variant="h5" fontWeight={950} sx={{ mb: 2 }}>
          {t('title')}
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 360px' }, gap: 2 }}>
          <Box>
            <Paper sx={{ p: 2.5, mb: 2, borderRadius: tplStyles.card.borderRadius, border: '1px solid rgba(16,42,67,0.1)' }} elevation={0}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
                <Typography fontWeight={950}>{t('form.contact_info')}</Typography>
              </Stack>

              <Stack spacing={2}>
                <TextField
                  label={t('form.phone')}
                  value={form.customer_phone}
                  inputProps={{ 'data-testid': 'checkout-phone', name: 'customer_phone' }}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setForm((p) => ({ ...p, customer_phone: v }));
                    setPhoneVerified(false);
                    setShowVerification(false);
                    setVerificationCode('');
                  }}
                  placeholder="09xxxxxxxx"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {phoneVerified ? (
                          <Chip label={t('form.verified')} color="success" size="small" />
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            disabled={verifying || countdown > 0}
                            onClick={sendVerificationCode}
                          >
                            {countdown > 0 ? `${countdown}s` : t('form.send_code')}
                          </Button>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                {showVerification && !phoneVerified && (
                  <TextField
                    label={t('form.verification_code')}
                    value={verificationCode}
                    inputProps={{ 'data-testid': 'checkout-code' }}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button variant="outlined" size="small" disabled={verifying} onClick={verifyPhone}>
                            {t('form.verify')}
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    required
                    label={t('shipping.recipient_name')}
                    value={form.customer_name}
                    inputProps={{ 'data-testid': 'checkout-name', name: 'customer_name' }}
                    onChange={(e) => setForm((p) => ({ ...p, customer_name: e.target.value }))}
                    placeholder={t('form.name_placeholder')}
                  />
                  <TextField
                    fullWidth
                    label={t('form.email_optional')}
                    type="email"
                    value={form.customer_email}
                    inputProps={{ 'data-testid': 'checkout-email', name: 'customer_email' }}
                    onChange={(e) => setForm((p) => ({ ...p, customer_email: e.target.value }))}
                    placeholder={t('form.email_placeholder')}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Paper sx={{ p: 2.5, mb: 2, borderRadius: tplStyles.card.borderRadius, border: '1px solid rgba(16,42,67,0.1)' }} elevation={0}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: '#ff9800', width: 32, height: 32 }}>
                  <LocalShippingIcon fontSize="small" />
                </Avatar>
                <Typography fontWeight={950}>{t('shipping.title')}</Typography>
              </Stack>

              <RadioGroup
                row
                value={form.shipping_method}
                onChange={(e) => setForm((p) => ({ ...p, shipping_method: e.target.value }))}
                data-testid="checkout-shipping-method"
                sx={{ mb: 2 }}
              >
                <FormControlLabel value="cvs" control={<Radio />} label={t('shipping.cvs_pickup')} data-testid="shipping-cvs" />
                <FormControlLabel value="home" control={<Radio />} label={t('shipping.home_delivery')} data-testid="shipping-home" />
              </RadioGroup>

              {form.shipping_method === 'cvs' && (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel>{t('shipping.cvs_type')}</InputLabel>
                      <Select
                        name="store_type"
                        value={form.store_type}
                        label={t('shipping.cvs_type')}
                        onChange={(e) => setForm((p) => ({ ...p, store_type: e.target.value }))}
                      >
                        <MenuItem value="7-11">7-ELEVEN</MenuItem>
                        <MenuItem value="family">{t('shipping.family_mart')}</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      startIcon={emapLoading ? <CircularProgress size={18} color="inherit" /> : <OpenInNewIcon />}
                      onClick={handleOpenStoreMap}
                      disabled={emapLoading}
                      sx={{ height: 56, fontWeight: 950 }}
                      fullWidth
                    >
                      {emapLoading ? t('shipping.opening') : t('shipping.select_store')}
                    </Button>
                  </Stack>

                  {!form.store_name ? (
                    <Alert severity="info">{t('shipping.select_store_hint')}</Alert>
                  ) : (
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                      {t('shipping.selected')}: {form.store_name}
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {form.store_address}
                      </Typography>
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => setForm((p) => ({ ...p, store_id: '', store_name: '', store_address: '' }))}
                      >
                        {t('shipping.reselect')}
                      </Button>
                    </Alert>
                  )}
                </Stack>
              )}

              {form.shipping_method === 'home' && (
                <TaiwanAddressSelector
                  city={form.home_city}
                  district={form.home_district}
                  zipcode={form.home_zipcode}
                  address={form.home_address}
                  onCityChange={(v) => setForm((p) => ({ ...p, home_city: v }))}
                  onDistrictChange={(v) => setForm((p) => ({ ...p, home_district: v }))}
                  onZipcodeChange={(v) => setForm((p) => ({ ...p, home_zipcode: v }))}
                  onAddressChange={(v) => setForm((p) => ({ ...p, home_address: v }))}
                  required
                />
              )}
            </Paper>

            <Paper sx={{ p: 2.5, mb: 2, borderRadius: tplStyles.card.borderRadius, border: '1px solid rgba(16,42,67,0.1)' }} elevation={0}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: '#0E7490', width: 32, height: 32 }}>
                  <PaymentIcon fontSize="small" />
                </Avatar>
                <Typography fontWeight={950}>{t('payment.title')}</Typography>
              </Stack>

              {loadingPayments ? (
                <Box>
                  <Skeleton height={70} sx={{ borderRadius: 2 }} />
                  <Skeleton height={70} sx={{ borderRadius: 2, mt: 1 }} />
                </Box>
              ) : (
                <RadioGroup
                  name="payment_method"
                  value={form.payment_method}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, payment_method: e.target.value }));
                    if (!hasTrackedPayment.current && cart && window.dataLayer) {
                      hasTrackedPayment.current = true;
                      window.dataLayer.push({ ecommerce: null });
                      window.dataLayer.push({
                        event: 'add_payment_info',
                        ecommerce: {
                          currency: 'TWD',
                          value: cart.subtotal,
                          payment_type: e.target.value,
                          items: cart.items.map((item) => ({
                            item_id: item.sku,
                            item_name: item.name,
                            price: item.sale_price,
                            quantity: item.quantity,
                          })),
                        },
                      });
                    }
                  }}
                >
                  {paymentMethods.map((m) => (
                    <Card
                      key={m.code}
                      data-testid={`payment-method-${m.code}`}
                      onClick={() => setForm((p) => ({ ...p, payment_method: m.code }))}
                      sx={{
                        mb: 1,
                        borderRadius: tplStyles.card.borderRadius,
                        border: form.payment_method === m.code ? '2px solid' : '1px solid',
                        borderColor: form.payment_method === m.code ? 'primary.main' : 'divider',
                        background: form.payment_method === m.code ? 'linear-gradient(180deg, #F8FCFB 0%, #FFFFFF 100%)' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': { borderColor: 'primary.main', boxShadow: '0 10px 20px rgba(16,42,67,0.08)' },
                      }}
                      elevation={0}
                    >
                      <CardContent sx={{ py: 1.5 }}>
                        <FormControlLabel
                          value={m.code}
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography sx={{ fontSize: 22 }}>{m.icon || '💳'}</Typography>
                              <Box>
                                <Typography fontWeight={900}>{m.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {m.description}
                                </Typography>
                              </Box>
                            </Box>
                          }
                          sx={{ m: 0, width: '100%' }}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </RadioGroup>
              )}
            </Paper>

            <Paper sx={{ p: 2.5, mb: 2, borderRadius: tplStyles.card.borderRadius, border: '1px solid rgba(16,42,67,0.1)' }} elevation={0}>
              <EInvoiceSelector value={invoiceData} onChange={setInvoiceData} />
            </Paper>

            <Paper sx={{ p: 2.5, borderRadius: tplStyles.card.borderRadius, border: '1px solid rgba(16,42,67,0.1)' }} elevation={0}>
              <TextField
                fullWidth
                label={t('confirm.order_note')}
                multiline
                rows={2}
                value={form.remark}
                inputProps={{ 'data-testid': 'checkout-remark', name: 'remark' }}
                onChange={(e) => setForm((p) => ({ ...p, remark: e.target.value }))}
                placeholder={t('confirm.order_note_placeholder')}
              />
            </Paper>
          </Box>

          <Box>{summary}</Box>
        </Box>
      </Container>

      {isMobile && (
        <Paper
          elevation={10}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 56,
            borderTop: '1px solid',
            borderColor: 'divider',
            p: 1.5,
            zIndex: 1090,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {t('cart.total')}
              </Typography>
              <Typography fontWeight={950} color="error" sx={{ lineHeight: 1.2 }}>
                ${Number(total).toLocaleString()}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSubmit}
              disabled={loading || loadingPayments || !phoneVerified}
              sx={{ py: 1.2, fontWeight: 950, borderRadius: 2.5 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : submitButtonText}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* 彈出視窗阻擋防護機制 Dialog */}
      <Dialog open={!!blockedUrl} onClose={() => setBlockedUrl('')}>
        <DialogTitle>{t('shipping.map_blocked_title', '無法開啟地圖')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('shipping.map_blocked_desc', '您的瀏覽器攔截了超商電子地圖的彈出視窗。請點擊下方按鈕手動開啟：')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockedUrl('')} color="inherit">
            {t('cancel', '取消')}
          </Button>
          <Button
            onClick={() => {
              window.open(blockedUrl, 'StoreMap', 'width=900,height=700,scrollbars=yes,resizable=yes');
              setBlockedUrl('');
            }}
            color="primary"
            variant="contained"
          >
            {t('shipping.open_map_manually', '手動開啟')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Checkout;
